import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Trash } from "iconsax-react";
import { FileUploader } from "@/shared/components";
import { Button, Input, ToggleButton } from "@/shared/ui";
import type { ScientificWorkRecord } from "../../../types/personalSheet";
import { editScientificWorkEntrySchema } from "./validation";
import type { SectionFormHandle } from "./types";

interface Props {
  records: ScientificWorkRecord[];
  initialHasNoScientificWorks?: boolean;
  recordsEditable?: boolean;
  draftAddedRecords?: ScientificWorkRecord[];
}

interface ScientificWorkEntry {
  id: number | null;
  title: string;
  year: string;
  existingFileUrl: string | null;
  newFile: File | null;
  fileRemoved: boolean;
  fileToken: string | null;
}

function makeEmptyEntry(): ScientificWorkEntry {
  return { id: null, title: "", year: "", existingFileUrl: null, newFile: null, fileRemoved: false, fileToken: null };
}

function fromRecord(record: ScientificWorkRecord): ScientificWorkEntry {
  return { id: record.id, title: record.title, year: record.year, existingFileUrl: record.file_url, newFile: null, fileRemoved: false, fileToken: record.file_token ?? null };
}

function isFilledEntry(entry: ScientificWorkEntry): boolean {
  return entry.title.trim() !== "";
}

function toApiEntry(entry: ScientificWorkEntry): Record<string, unknown> {
  const api: Record<string, unknown> = { title: entry.title, year: entry.year };
  if (entry.id != null) api.id = entry.id;
  if (entry.newFile && entry.fileToken) api.file_token = entry.fileToken;
  else if (entry.fileRemoved) api.file = null;
  else if (entry.fileToken) api.file_token = entry.fileToken;
  return api;
}

function fileSignature(entry: ScientificWorkEntry): string {
  if (entry.newFile) return `new:${entry.fileToken}`;
  if (entry.fileRemoved) return "removed";
  return entry.existingFileUrl ? "keep" : "none";
}

function entriesSignature(entries: ScientificWorkEntry[]): string {
  return JSON.stringify(
    entries.filter(isFilledEntry).map((e) => ({ id: e.id, title: e.title, year: e.year, file: fileSignature(e) })),
  );
}

const EditScientificWorksSection = forwardRef<SectionFormHandle, Props>(function EditScientificWorksSection(
  { records, initialHasNoScientificWorks, recordsEditable = false, draftAddedRecords },
  ref,
) {
  const { t } = useTranslation("EmployeePortal");

  const [hasNoScientificWorks, setHasNoScientificWorks] = useState(initialHasNoScientificWorks ?? false);
  const [newEntries, setNewEntries] = useState<ScientificWorkEntry[]>(() =>
    recordsEditable ? records.map(fromRecord) : (draftAddedRecords ?? []).map(fromRecord),
  );
  const initialSignatureRef = useRef<string>(entriesSignature(recordsEditable ? records.map(fromRecord) : []));
  const fileTokenRef = useRef(0);
  const [existingFileChanges, setExistingFileChanges] = useState<Record<number, File>>({});
  const [removedExistingFiles, setRemovedExistingFiles] = useState<Set<number>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Record<number, Record<string, string>>>({});

  function handleAddEntry() {
    setNewEntries((prev) => [...prev, makeEmptyEntry()]);
  }

  function handleRemoveEntry(index: number) {
    setNewEntries((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function updateEntry(index: number, field: "title" | "year", value: string) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  }

  function setEntryFile(index: number, file: File | null) {
    const token = file ? `f${(fileTokenRef.current += 1)}` : null;
    setNewEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, newFile: file, fileToken: token, fileRemoved: file ? false : entry.fileRemoved } : entry,
      ),
    );
  }

  function setEntryFileRemoved(index: number) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, fileRemoved: true } : entry)));
  }

  function updateExistingFile(recordIndex: number, file: File | null) {
    setExistingFileChanges((prev) => {
      if (file === null) {
        const next = { ...prev };
        delete next[recordIndex];
        return next;
      }
      return { ...prev, [recordIndex]: file };
    });
  }

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        if (hasNoScientificWorks) return [] as unknown as Record<string, unknown>;
        if (recordsEditable) {
          if (entriesSignature(newEntries) === initialSignatureRef.current) return null;
          return newEntries.filter(isFilledEntry).map(toApiEntry) as unknown as Record<string, unknown>;
        }
        const hasNewFilledEntries = newEntries.some(isFilledEntry);
        const hasExistingFileChanges = Object.keys(existingFileChanges).length > 0;
        if (!hasNewFilledEntries && !hasExistingFileChanges) return null;
        const allRecords = [
          ...records.map((r) => ({ title: r.title, year: r.year })),
          ...newEntries.filter(isFilledEntry).map((e) => ({ title: e.title, year: e.year })),
        ];
        return allRecords as unknown as Record<string, unknown>;
      },
      getFiles() {
        if (hasNoScientificWorks) return null;
        const files: Record<string, File> = {};
        if (recordsEditable) {
          newEntries.filter(isFilledEntry).forEach((entry) => {
            if (entry.newFile && entry.fileToken) {
              files[`scientific_works.${entry.fileToken}.file`] = entry.newFile;
            }
          });
        } else {
          for (const [idx, file] of Object.entries(existingFileChanges)) {
            files[`scientific_works.${idx}.file`] = file;
          }
          newEntries.forEach((entry, i) => {
            if (entry.newFile) {
              files[`scientific_works.${records.length + i}.file`] = entry.newFile;
            }
          });
        }
        return Object.keys(files).length > 0 ? files : null;
      },
      async validate() {
        if (hasNoScientificWorks) {
          setValidationErrors({});
          return true;
        }
        const errors: Record<number, Record<string, string>> = {};
        let allValid = true;
        for (let i = 0; i < newEntries.length; i++) {
          const entry = newEntries[i];
          if (!isFilledEntry(entry)) continue;
          const entryErrors: Record<string, string> = {};
          const result = editScientificWorkEntrySchema.safeParse({ title: entry.title, year: entry.year });
          if (!result.success) {
            for (const issue of result.error.issues) {
              entryErrors[issue.path[0] as string] = issue.message;
            }
          }
          const fileOk = recordsEditable
            ? (!!entry.existingFileUrl && !entry.fileRemoved) || !!entry.newFile
            : !!entry.newFile || ((!!entry.fileToken || !!entry.existingFileUrl) && !entry.fileRemoved);
          if (!fileOk) entryErrors.file = "personalSheet.validation.required";
          if (Object.keys(entryErrors).length > 0) {
            allValid = false;
            errors[i] = entryErrors;
          }
        }
        setValidationErrors(errors);
        return allValid;
      },
      isDirty() {
        const flagChanged = hasNoScientificWorks !== (initialHasNoScientificWorks ?? false);
        if (recordsEditable) return flagChanged || entriesSignature(newEntries) !== initialSignatureRef.current;
        return flagChanged || newEntries.some(isFilledEntry) || Object.keys(existingFileChanges).length > 0;
      },
      getFlagChanges() {
        if (hasNoScientificWorks !== (initialHasNoScientificWorks ?? false)) {
          return { has_no_scientific_works: hasNoScientificWorks };
        }
        return null;
      },
    };
  });

  const showReadOnly = !recordsEditable;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <span className="text-label-sm content-base-secondary">{t("personalSheet.hasNoScientificWorks")}</span>
        <ToggleButton checked={hasNoScientificWorks} onChange={setHasNoScientificWorks} />
      </div>

      {hasNoScientificWorks ? (
        <p className="text-body-regular-sm content-base-secondary p-3 surface-secondary-fill radius-sm">
          {t("personalSheet.noScientificWorksNote")}
        </p>
      ) : (
        <>
          {showReadOnly &&
            records.map((record, index) => (
              <div key={record.id} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
                <p className="text-body-bold-md content-base-primary">
                  {t("personalSheet.scientificWorkRecord", { number: index + 1 })}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label={t("personalSheet.fields.scientificWorkTitle")} placeholder="" value={record.title} disabled />
                  <Input label={t("personalSheet.fields.scientificWorkYear")} placeholder="" value={record.year} disabled />
                </div>
                <FileUploader
                  label={t("personalSheet.fields.scientificWorkFile")}
                  existingFileUrl={removedExistingFiles.has(index) ? null : record.file_url}
                  value={existingFileChanges[index] ?? null}
                  onChange={(file) => updateExistingFile(index, file)}
                  onRemoveExisting={() => setRemovedExistingFiles((prev) => new Set(prev).add(index))}
                />
              </div>
            ))}

          {newEntries.map((entry, index) => (
            <div key={index} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className="text-body-bold-md content-base-primary">
                  {t("personalSheet.scientificWorkRecord", { number: (showReadOnly ? records.length : 0) + index + 1 })}
                </p>
                <Button variant="danger" size="sm" onClick={() => handleRemoveEntry(index)}>
                  <Trash size={16} color="currentColor" />
                  {t("personalSheet.removeRecord")}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t("personalSheet.fields.scientificWorkTitle")}
                  hasRequiredLabel
                  placeholder={t("personalSheet.fields.scientificWorkTitle")}
                  value={entry.title}
                  onChange={(e) => updateEntry(index, "title", e.target.value)}
                  error={validationErrors[index]?.title ? t(validationErrors[index].title) : undefined}
                />
                <Input
                  label={t("personalSheet.fields.scientificWorkYear")}
                  hasRequiredLabel
                  placeholder={t("personalSheet.fields.scientificWorkYear")}
                  value={entry.year}
                  onChange={(e) => updateEntry(index, "year", e.target.value)}
                  error={validationErrors[index]?.year ? t(validationErrors[index].year) : undefined}
                />
              </div>
              <FileUploader
                label={t("personalSheet.fields.scientificWorkFile")}
                hasRequiredLabel
                existingFileUrl={entry.fileRemoved ? null : entry.existingFileUrl}
                value={entry.newFile}
                onChange={(file) => setEntryFile(index, file)}
                onRemoveExisting={() => setEntryFileRemoved(index)}
                error={validationErrors[index]?.file ? t(validationErrors[index].file) : undefined}
                namespace="EmployeePortal"
              />
            </div>
          ))}

          <Button variant="tertiary" size="lg" onClick={handleAddEntry} className="w-full text-center">
            <Add size={16} color="currentColor" />
            {t("personalSheet.addScientificWork")}
          </Button>
        </>
      )}
    </div>
  );
});

export default EditScientificWorksSection;
